import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, FieldboxWithRadio, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'

import { Stack } from '@mui/material'
import { HandleOpenOrdersOption } from '@planning/app/api'
import { OrderItemJourneyStatusChipWrapper } from '@planning/components/molecules/ContainerJourneyStatusChipFFWrapper'
import { OrderItemChips } from '@planning/components/organisms/OrderItemChips'
import { useUnitTypeIcon } from '@planning/components/UnitType'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { orderService } from '@planning/services'

interface Props {
  visitItem: IVesselVisitItem | IRailVisitItem
  callback?: (
    handleInboundOpenOrdersOption: HandleOpenOrdersOption,
    handleOutboundOpenOrdersOption: HandleOpenOrdersOption,
    handleRestowOpenOrdersOption: HandleOpenOrdersOption,
  ) => Promise<void>
  hideFinishOrderOptionForLoading?: boolean
}

enum OrderJourneyCategory {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
  Restow = 'Restow',
}

export const RemoveOrFinishOpenOrders: FC<Props> = observer(
  ({ visitItem, callback, hideFinishOrderOptionForLoading }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { dialogStore, appViewStore } = usePlanningStore()

    const [carrierVisitDirection, setCarrierVisitDirection] = useState<OrderJourneyCategory>(
      visitItem.restowOpenOrders?.length
        ? OrderJourneyCategory.Restow
        : visitItem.outboundOpenOrders.length
          ? OrderJourneyCategory.Outbound
          : OrderJourneyCategory.Inbound,
    )
    const [inboundRemoveOrFinishOrderOption, setInboundRemoveOrFinishOrderOption] = useState<
      HandleOpenOrdersOption | undefined
    >()

    const [outboundRemoveOrFinishOrderOption, setOutboundRemoveOrFinishOrderOption] = useState<
      HandleOpenOrdersOption | undefined
    >()

    const [restowRemoveOrFinishOrderOption, setRestowRemoveOrFinishOrderOption] = useState<
      HandleOpenOrdersOption | undefined
    >()

    useEffect(() => {
      setInboundRemoveOrFinishOrderOption(undefined)
      setOutboundRemoveOrFinishOrderOption(undefined)
      setRestowRemoveOrFinishOrderOption(undefined)
    }, [])

    const selectedOrders =
      carrierVisitDirection === OrderJourneyCategory.Inbound
        ? visitItem.inboundOpenOrders
        : carrierVisitDirection === OrderJourneyCategory.Outbound
          ? visitItem.outboundOpenOrders
          : visitItem.restowOpenOrders ?? []

    const selectedRemoveOrFinishOrderOption =
      carrierVisitDirection === OrderJourneyCategory.Inbound
        ? inboundRemoveOrFinishOrderOption
        : carrierVisitDirection === OrderJourneyCategory.Outbound
          ? outboundRemoveOrFinishOrderOption
          : restowRemoveOrFinishOrderOption

    const handleOptionChange = (value: string): void => {
      if (carrierVisitDirection === OrderJourneyCategory.Inbound)
        setInboundRemoveOrFinishOrderOption(value as HandleOpenOrdersOption)
      else if (carrierVisitDirection === OrderJourneyCategory.Outbound)
        setOutboundRemoveOrFinishOrderOption(value as HandleOpenOrdersOption)
      else setRestowRemoveOrFinishOrderOption(value as HandleOpenOrdersOption)
    }

    const handleConfirm = async () => {
      try {
        await orderService.removeOrFinishOpenOrders({
          carrierVisitId: visitItem.id,
          inboundRemoveOrFinishOrderOption: inboundRemoveOrFinishOrderOption,
          outboundRemoveOrFinishOrderOption: outboundRemoveOrFinishOrderOption,
          restowRemoveOrFinishOrderOption: restowRemoveOrFinishOrderOption,
        })
        if (callback)
          await callback(
            inboundRemoveOrFinishOrderOption ?? HandleOpenOrdersOption.Remove,
            outboundRemoveOrFinishOrderOption ?? HandleOpenOrdersOption.Remove,
            restowRemoveOrFinishOrderOption ?? HandleOpenOrdersOption.Remove,
          )
        appViewStore.setShowAlert(
          'success',
          t('operationEnded', 'Operations were successfully ended.'),
        )
        dialogStore.close()
      } catch (error: any) {
        appViewStore.setShowAlert(
          'error',
          error.response?.data?.errors?.validationError ?? 'An unexpected error occurred',
        )
      }
    }

    return (
      <>
        <Typography variant='h4' m={theme.spacing(3)} align='left'>
          {t('containersWithStatusInProgress', 'Containers with status in progress')}
        </Typography>
        <Divider />
        <DialogContent>
          <Box mt={theme.spacing(1)} width='100%'>
            <Stack spacing={theme.spacing(3)} width='100%'>
              <Stack spacing={theme.spacing(1)} direction='row'>
                <Button
                  variant='contained'
                  disabled={visitItem.outboundOpenOrders.length === 0}
                  onClick={() => setCarrierVisitDirection(OrderJourneyCategory.Outbound)}
                  color={
                    carrierVisitDirection === OrderJourneyCategory.Outbound
                      ? 'secondary'
                      : 'inherit'
                  }
                >
                  {t('loading', 'Loading')} {visitItem.outboundOpenOrders.length}
                </Button>
                <Button
                  variant='contained'
                  disabled={visitItem.inboundOpenOrders.length === 0}
                  onClick={() => setCarrierVisitDirection(OrderJourneyCategory.Inbound)}
                  color={
                    carrierVisitDirection === OrderJourneyCategory.Inbound ? 'secondary' : 'inherit'
                  }
                >
                  {t('discharge', 'Discharge')} {visitItem.inboundOpenOrders.length}
                </Button>
                {!!visitItem.restowOpenOrders?.length && (
                  <Button
                    variant='contained'
                    disabled={visitItem.restowOpenOrders?.length === 0}
                    onClick={() => setCarrierVisitDirection(OrderJourneyCategory.Restow)}
                    color={
                      carrierVisitDirection === OrderJourneyCategory.Restow
                        ? 'secondary'
                        : 'inherit'
                    }
                  >
                    {t('restow', 'Restow')} {visitItem.restowOpenOrders?.length ?? 0}
                  </Button>
                )}
              </Stack>
              <Alert severity='warning'>
                {t(
                  'thereAreStillContainerWithTheStatusInProgress.',
                  'There are still container with the status in progress.',
                )}
              </Alert>
              <Paper variant='outlined' sx={{ width: '100%' }}>
                {selectedOrders.map(item => (
                  <OrderItem item={item} key={`grp-row-${item.id}`} />
                ))}
              </Paper>
              <Stack>
                <FieldboxWithRadio
                  selectionKey={HandleOpenOrdersOption.Remove}
                  selectedKey={selectedRemoveOrFinishOrderOption}
                  onSelect={handleOptionChange}
                  label={
                    carrierVisitDirection === OrderJourneyCategory.Restow
                      ? t('transformToImport', 'Transform to import')
                      : t('removeOrders', 'Remove orders')
                  }
                  dataCy='remove-orders-radio-btn'
                  description={
                    carrierVisitDirection === OrderJourneyCategory.Restow
                      ? t(
                          'theRestowOrdersWillBeTransformedToImport.',
                          'The restow orders will be transformed to import.',
                        )
                      : t(
                          'theOrdersWillBeDeletedAndWorkInstructionsWillBeCancelled.',
                          'The orders will be deleted and work instructions will be cancelled.',
                        )
                  }
                />

                {hideFinishOrderOptionForLoading &&
                carrierVisitDirection === OrderJourneyCategory.Outbound ? (
                  <></>
                ) : (
                  <FieldboxWithRadio
                    selectionKey={HandleOpenOrdersOption.Finish}
                    selectedKey={selectedRemoveOrFinishOrderOption}
                    onSelect={handleOptionChange}
                    label={t('markAsDone', 'Mark as done')}
                    description={
                      carrierVisitDirection === OrderJourneyCategory.Inbound
                        ? t(
                            'theOrdersWillBeFulfilledAndWorkInstructionsWillRemainOpen.',
                            'The orders will be fulfilled and work instructions will remain open but with issues so the Yard Position can be provided.',
                          )
                        : t(
                            'theOrdersWillBeFulfilledAndWorkInstructionsWillBeFinished.OrdersWithTheStatusExpectedWillBeDeletedAndWorkInstructionsWillBeCancelled',
                            'The orders will be fulfilled and work instructions will be finished. Orders with the status "Expected" will be deleted and work instructions will be cancelled',
                          )
                    }
                  />
                )}
              </Stack>
            </Stack>
          </Box>
        </DialogContent>
        <Divider />
        <Grid container>
          <Grid size={11}>
            <DialogActions>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleConfirm}
                  data-cy='remove-or-finish-submit-btn'
                >
                  {t('confirm', 'Confirm')}
                </Button>

                <Button variant='text' color='inherit' onClick={dialogStore.close}>
                  {t('cancel', 'Cancel')}
                </Button>
              </Box>
            </DialogActions>
          </Grid>
        </Grid>
      </>
    )
  },
)

const OrderItem: FC<{ item: IOrderItem }> = observer(({ item }: { item: IOrderItem }) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  return (
    <Stack
      direction='row'
      paddingX={theme.customSpacing.m}
      paddingY={theme.customSpacing.xxs}
      justifyContent='space-between'
      alignItems='center'
      gap={theme.customSpacing.m}
      width='100%'
      borderBottom={`1px solid ${theme.palette.grey[300]}`}
    >
      <Stack direction='row' alignItems='center' gap={theme.customSpacing.m}>
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <Box key={item.id}>{useUnitTypeIcon(item.data.unitType)}</Box>
          <Box width='100%'>
            <Typography component={'div'} width='100%' variant='body2' minWidth='7rem'>
              {item.data.containerNumber ??
                (item.data.nonNumericOrderId ? t('nonNumeric', 'Non-numeric') : '')}
            </Typography>
            <Typography component={'div'} width='100%' variant='mini'>
              {item.data.referenceNumber ? item.data.referenceNumber : EmptyIndicator}
            </Typography>
          </Box>
        </Stack>
        <OrderItemChips order={item.data} />
      </Stack>
      <Box sx={{ flex: 'nowrap' }}>
        <OrderItemJourneyStatusChipWrapper orderItem={item} />
      </Box>
    </Stack>
  )
})
